import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Input,
  Alert,
  Badge,
  Typography,
  Space,
  Spin,
  Tag,
  Row,
  Col,
  Grid,
  Tooltip,
  message
} from 'antd';
import {
  CopyOutlined,
  DownloadOutlined,
  EyeOutlined,
  ReloadOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { apiRequests } from "src/shared/api/api";

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const FeedPreview = ({ feed, token }) => {
  const [xmlData, setXmlData] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const screens = useBreakpoint();

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiRequests.feeds.getFeedXml(feed.url_token);
      setXmlData(response.data);
    } catch (error) {
      setXmlData('Ошибка загрузки XML. Проверьте соединение или настройки фида.');
      message.error('Ошибка загрузки XML');
    } finally {
      setLoading(false);
    }
  }, [feed?.url_token]);

  useEffect(() => {
    if (feed?.url_token) {
      loadPreview();
    }
  }, [feed, loadPreview]);

  const copyToClipboard = () => {
    if (xmlData) {
      navigator.clipboard.writeText(xmlData);
      setCopied(true);
      message.success('XML скопирован в буфер обмена');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadXml = () => {
    if (xmlData && feed) {
      const blob = new Blob([xmlData], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feed-${feed.name || 'preview'}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('XML скачивается');
    }
  };

  const getFeedUrl = () => {
    if (!feed?.url_token) return '';
    const baseUrl = window.location.origin.includes('localhost') 
      ? 'https://dev.tablecrm.com/api/v1'
      : window.location.origin + '/api/v1';
    return `${baseUrl}/feeds/${feed.url_token}?token=${token}`;
  };

  if (!feed) {
    return (
      <Alert
        message="Выберите фид"
        description="Выберите фид из списка для предпросмотра XML"
        type="info"
        className='text-center items-center'
      />
    );
  }

  return (
    <Card 
      className="w-full"
      title={
        <div className="flex justify-between items-center">
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Предпросмотр фида: {feed.name}
            </Title>
            <Text type="secondary">
              {feed.description || 'XML выгрузка товаров'}
            </Text>
          </div>
          <Badge 
            status={feed.url_token ? "success" : "default"}
            text={feed.url_token ? 'Опубликован' : 'Черновик'}
          />
        </div>
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* URL фида */}
        <Card size="small" title="URL фида">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>Используйте этот URL для интеграции с маркетплейсами</Text>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={getFeedUrl() || 'Не опубликован'}
                readOnly
                style={{ fontFamily: 'monospace' }}
              />
              {feed.url_token && (
                <Tooltip title="Открыть в новой вкладке">
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => window.open(getFeedUrl(), '_blank')}
                  />
                </Tooltip>
              )}
            </Space.Compact>
          </Space>
        </Card>

        {/* Панель управления */}
        <Row justify="space-between" align="middle">
          <Col>
            <Text type="secondary">
              Структура: <Text code>{feed.root_tag} → {feed.item_tag}</Text>
            </Text>
          </Col>
          <Col>
            <Space wrap={screens.xs}>
              <Button
                icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                onClick={copyToClipboard}
                disabled={!xmlData || loading}
              >
                {copied ? 'Скопировано' : 'Копировать'}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadXml}
                disabled={!xmlData || loading}
              >
                Скачать XML
              </Button>
              <Button
                icon={loading ? <Spin size="small" /> : <ReloadOutlined />}
                onClick={loadPreview}
                disabled={loading}
              >
                Обновить
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Предпросмотр XML */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <Paragraph type="secondary" style={{ marginTop: 16 }}>
              Загрузка актуального XML...
            </Paragraph>
          </div>
        ) : (
          <Card 
            size="small" 
            title="Предпросмотр XML"
            bodyStyle={{ 
              padding: 0,
              maxHeight: '500px',
              overflow: 'auto'
            }}
          >
            <pre style={{
              margin: 0,
              padding: 16,
              fontSize: '12px',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              backgroundColor: '#f6f8fa'
            }}>
              {xmlData || 'XML не загружен'}
            </pre>
          </Card>
        )}

        {/* Статистика фида */}
        <Card size="small">
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <Space direction="vertical" size={0}>
                <Text type="secondary">Корневой тег</Text>
                <Text strong code>{feed.root_tag}</Text>
              </Space>
            </Col>
            <Col xs={12} sm={6}>
              <Space direction="vertical" size={0}>
                <Text type="secondary">Тег товара</Text>
                <Text strong code>{feed.item_tag}</Text>
              </Space>
            </Col>
            <Col xs={12} sm={6}>
              <Space direction="vertical" size={0}>
                <Text type="secondary">Поля</Text>
                <Text strong>
                  {Object.keys(feed.field_tags || {}).length}
                </Text>
              </Space>
            </Col>
            <Col xs={12} sm={6}>
              <Space direction="vertical" size={0}>
                <Text type="secondary">Статус</Text>
                <Tag color={feed.url_token ? "green" : "default"}>
                  {feed.url_token ? 'Активен' : 'Черновик'}
                </Tag>
              </Space>
            </Col>
          </Row>
        </Card>
      </Space>
    </Card>
  );
};

export default FeedPreview;